/**
 * Async helpers for non-blocking operations
 * Used in P0-03 performance optimizations
 * 
 * @author AI Assistant
 * @date 2026-01-07
 */

/**
 * Yield control back to the main thread to prevent blocking
 * Uses scheduler.yield() if available (Chrome 94+), falls back to setTimeout
 * 
 * @returns Promise that resolves after yielding
 */
export async function yieldToMainThread(): Promise<void> {
  // Check for scheduler.yield() support (modern browsers)
  if ('scheduler' in window && 'yield' in (window as any).scheduler) {
    return (window as any).scheduler.yield();
  }
  
  // Fallback to setTimeout(0)
  return new Promise<void>(resolve => setTimeout(resolve, 0));
}

/**
 * Process array items in chunks with yields between chunks
 * Prevents long tasks by breaking work into smaller pieces
 * 
 * @param items - Array of items to process
 * @param chunkSize - Number of items to process before yielding
 * @param fn - Function to apply to each item
 * @param signal - Optional AbortSignal for cancellation (RQ-03)
 * 
 * @example
 * await forEachChunked(questions, 25, (q) => {
 *   this.evaluateQuestion(q);
 * }, abortSignal);
 */
export async function forEachChunked<T>(
  items: T[],
  chunkSize: number,
  fn: (item: T, index: number) => void,
  signal?: AbortSignal
): Promise<void> {
  for (let i = 0; i < items.length; i += chunkSize) {
    // Check cancellation before each chunk (RQ-03)
    if (signal?.aborted) return;
    
    const chunkEnd = Math.min(i + chunkSize, items.length);
    
    // Process chunk
    for (let j = i; j < chunkEnd; j++) {
      fn(items[j], j);
    }
    
    // Yield to main thread after each chunk
    if (chunkEnd < items.length) {
      await yieldToMainThread();
    }
  }
}

/**
 * Process array items in chunks and return mapped results
 * Similar to forEachChunked but returns transformed array
 * 
 * @param items - Array of items to process
 * @param chunkSize - Number of items to process before yielding
 * @param fn - Transform function to apply to each item
 * @returns Array of transformed items
 */
export async function mapChunked<T, R>(
  items: T[],
  chunkSize: number,
  fn: (item: T, index: number) => R
): Promise<R[]> {
  const results: R[] = [];
  
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunkEnd = Math.min(i + chunkSize, items.length);
    
    // Process chunk
    for (let j = i; j < chunkEnd; j++) {
      results.push(fn(items[j], j));
    }
    
    // Yield to main thread after each chunk
    if (chunkEnd < items.length) {
      await yieldToMainThread();
    }
  }
  
  return results;
}
